import React, { useState, useMemo } from 'react';
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  AppBar,
  Toolbar,
  Typography,
  Box,
  Paper,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  InputAdornment,
  Drawer,
  IconButton,
  useMediaQuery,
  useTheme,
  Divider,
  Tooltip,
  alpha,
} from '@mui/material';
import {
  Code,
  Link,
  Security,
  Calculate,
  Facebook,
  QrCode,
  Fingerprint,
  Schedule,
  Search,
  Menu as MenuIcon,
  PushPin,
  PushPinOutlined,
  Straighten,
  Terminal,
} from '@mui/icons-material';
import Base64Utility from './components/Base64Utility';
import UrlUtility from './components/UrlUtility';
import HashUtility from './components/HashUtility';
import JsonUtility from './components/JsonUtility';
import TextUtility from './components/TextUtility';
import FacebookSearchUtility from './components/FacebookSearchUtility';
import QrCodeUtility from './components/QrCodeUtility';
import UuidUtility from './components/UuidUtility';
import TimestampUtility from './components/TimestampUtility';
import UnitConverterUtility from './components/UnitConverterUtility';

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#00e5ff', // Electric Cyan
      contrastText: '#000',
    },
    secondary: {
      main: '#ff4081', // Hot Pink
    },
    background: {
      default: '#050608',
      paper: '#0d0f14',
    },
    text: {
      primary: '#e0e0e0',
      secondary: '#9e9e9e',
    },
    divider: 'rgba(255, 255, 255, 0.08)',
  },
  typography: {
    fontFamily: '"Plus Jakarta Sans", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 800,
      letterSpacing: '-0.02em',
    },
    h6: {
      fontWeight: 700,
      letterSpacing: '-0.01em',
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
    },
    body1: {
      lineHeight: 1.6,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarColor: '#2b2b2b #050608',
          '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
            borderRadius: 8,
            backgroundColor: '#2b2b2b',
            minHeight: 24,
            border: '2px solid transparent',
            backgroundClip: 'content-box',
          },
          '&::-webkit-scrollbar-thumb:focus, & *::-webkit-scrollbar-thumb:focus': {
            backgroundColor: '#404040',
          },
          '&::-webkit-scrollbar-thumb:active, & *::-webkit-scrollbar-thumb:active': {
            backgroundColor: '#404040',
          },
          '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover': {
            backgroundColor: '#404040',
          },
          '&::-webkit-scrollbar-corner, & *::-webkit-scrollbar-corner': {
            backgroundColor: 'transparent',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(255, 255, 255, 0.03)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
            },
            '&.Mui-focused': {
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
            },
          },
        },
      },
    },
  },
});

const DRAWER_WIDTH = 280;

interface UtilityItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  keywords: string[];
  component: React.ReactNode;
}

const utilities: UtilityItem[] = [
  { id: 'base64', label: 'Base64', icon: <Code />, keywords: ['base64', 'encode', 'decode', 'binary'], component: <Base64Utility /> },
  { id: 'url', label: 'URL', icon: <Link />, keywords: ['url', 'encode', 'decode', 'uri', 'link'], component: <UrlUtility /> },
  { id: 'hash', label: 'Hash', icon: <Security />, keywords: ['hash', 'md5', 'sha', 'sha256', 'sha512', 'crypto', 'checksum'], component: <HashUtility /> },
  { id: 'json', label: 'JSON', icon: <Code />, keywords: ['json', 'format', 'prettify', 'minify', 'validate'], component: <JsonUtility /> },
  { id: 'text', label: 'Text', icon: <Calculate />, keywords: ['text', 'case', 'uppercase', 'lowercase', 'count', 'words'], component: <TextUtility /> },
  { id: 'uuid', label: 'UUID', icon: <Fingerprint />, keywords: ['uuid', 'guid', 'generate', 'random', 'id'], component: <UuidUtility /> },
  { id: 'timestamp', label: 'Timestamp', icon: <Schedule />, keywords: ['timestamp', 'unix', 'epoch', 'date', 'time', 'convert'], component: <TimestampUtility /> },
  { id: 'units', label: 'Unit Converter', icon: <Straighten />, keywords: ['unit', 'convert', 'length', 'weight', 'temperature', 'data', 'bytes', 'meters', 'feet', 'celsius', 'fahrenheit'], component: <UnitConverterUtility /> },
  { id: 'facebook', label: 'Facebook Search', icon: <Facebook />, keywords: ['facebook', 'fb', 'search', 'social'], component: <FacebookSearchUtility /> },
  { id: 'qrcode', label: 'QR Code', icon: <QrCode />, keywords: ['qr', 'qrcode', 'barcode', 'generate', 'scan'], component: <QrCodeUtility /> },
];

function App() {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(() => {
    const saved = localStorage.getItem('daily-utilities-selected');
    return saved || 'base64';
  });
  const [pinnedIds, setPinnedIds] = useState<string[]>(() => {
    const saved = localStorage.getItem('daily-utilities-pinned');
    return saved ? JSON.parse(saved) : [];
  });

  const togglePin = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setPinnedIds((prev) => {
      const newPinned = prev.includes(id)
        ? prev.filter((p) => p !== id)
        : [...prev, id];
      localStorage.setItem('daily-utilities-pinned', JSON.stringify(newPinned));
      return newPinned;
    });
  };

  const { pinnedUtilities, unpinnedUtilities } = useMemo(() => {
    let filtered = utilities;
    if (search.trim()) {
      const searchLower = search.toLowerCase();
      filtered = utilities.filter(
        (util) =>
          util.label.toLowerCase().includes(searchLower) ||
          util.keywords.some((kw) => kw.includes(searchLower))
      );
    }
    return {
      pinnedUtilities: filtered.filter((u) => pinnedIds.includes(u.id)),
      unpinnedUtilities: filtered.filter((u) => !pinnedIds.includes(u.id)),
    };
  }, [search, pinnedIds]);

  const selectedUtility = utilities.find((u) => u.id === selectedId) || utilities[0];

  const handleSelect = (id: string) => {
    setSelectedId(id);
    localStorage.setItem('daily-utilities-selected', id);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const renderUtilityItem = (util: UtilityItem) => {
    const isPinned = pinnedIds.includes(util.id);
    const isSelected = selectedId === util.id;
    return (
      <ListItemButton
        key={util.id}
        selected={isSelected}
        onClick={() => handleSelect(util.id)}
        sx={{
          borderRadius: 1.5,
          mx: 1.5,
          mb: 0.75,
          py: 1,
          transition: 'all 0.2s ease-in-out',
          position: 'relative',
          border: '1px solid transparent',
          '&.Mui-selected': {
            backgroundColor: alpha(theme.palette.primary.main, 0.05),
            color: 'primary.main',
            borderColor: alpha(theme.palette.primary.main, 0.2),
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
            },
            '& .MuiListItemIcon-root': {
              color: 'primary.main',
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 6,
              top: '25%',
              bottom: '25%',
              width: 2,
              backgroundColor: 'primary.main',
              borderRadius: 2,
              boxShadow: (t) => `0 0 8px ${t.palette.primary.main}`,
            },
          },
          '& .pin-button': {
            opacity: isPinned ? 1 : 0,
            transition: 'opacity 0.2s',
          },
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            '& .pin-button': {
              opacity: 1,
            },
          },
        }}
      >
        <ListItemIcon sx={{ minWidth: 42, color: isSelected ? 'primary.main' : 'text.secondary' }}>
          {util.icon}
        </ListItemIcon>
        <ListItemText 
          primary={util.label} 
          primaryTypographyProps={{ 
            variant: 'body2', 
            fontWeight: isSelected ? 700 : 500,
            letterSpacing: '0.01em'
          }} 
        />
        <Tooltip title={isPinned ? 'Unpin' : 'Pin to top'}>
          <IconButton
            size="small"
            className="pin-button"
            onClick={(e) => togglePin(util.id, e)}
            sx={{
              color: isSelected ? 'primary.main' : 'text.secondary',
              p: 0.5,
              '&:hover': { color: 'primary.main' }
            }}
          >
            {isPinned ? <PushPin fontSize="inherit" /> : <PushPinOutlined fontSize="inherit" />}
          </IconButton>
        </Tooltip>
      </ListItemButton>
    );
  };

  const sidebarContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#0d0f14' }}>
      <Box sx={{ p: 3, pb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, px: 1 }}>
          <Box 
            sx={{ 
              width: 32, 
              height: 32, 
              borderRadius: 1, 
              bgcolor: 'primary.main', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              mr: 1.5,
              boxShadow: (t) => `0 0 15px ${alpha(t.palette.primary.main, 0.4)}`
            }}
          >
            <Terminal sx={{ color: 'black', fontSize: 20 }} />
          </Box>
          <Typography variant="h6" sx={{ color: 'white', fontSize: '1.1rem' }}>
            Utility Box
          </Typography>
        </Box>
        <TextField
          fullWidth
          size="small"
          placeholder="Search tools..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
            sx: { borderRadius: 2, fontSize: '0.875rem' }
          }}
        />
      </Box>
      <List sx={{ flex: 1, overflow: 'auto', pt: 1, pb: 2 }}>
        {pinnedUtilities.length > 0 && (
          <>
            <Typography variant="caption" sx={{ px: 3, py: 1, display: 'block', color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              Pinned
            </Typography>
            {pinnedUtilities.map(renderUtilityItem)}
            {unpinnedUtilities.length > 0 && <Divider sx={{ my: 2, mx: 3, opacity: 0.5 }} />}
          </>
        )}
        {unpinnedUtilities.length > 0 && (
          <>
            {pinnedUtilities.length > 0 && (
              <Typography variant="caption" sx={{ px: 3, py: 1, display: 'block', color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                All Tools
              </Typography>
            )}
            {unpinnedUtilities.map(renderUtilityItem)}
          </>
        )}
        {pinnedUtilities.length === 0 && unpinnedUtilities.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center', fontStyle: 'italic' }}>
            No tools matching your search
          </Typography>
        )}
      </List>
    </Box>
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
        {/* Mobile Drawer */}
        {isMobile ? (
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            sx={{
              '& .MuiDrawer-paper': {
                width: DRAWER_WIDTH,
                boxSizing: 'border-box',
                borderRight: '1px solid',
                borderColor: 'divider',
              },
            }}
          >
            {sidebarContent}
          </Drawer>
        ) : (
          /* Desktop Sidebar */
          <Box
            sx={{
              width: DRAWER_WIDTH,
              flexShrink: 0,
              borderRight: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              height: '100vh',
              position: 'sticky',
              top: 0,
            }}
          >
            {sidebarContent}
          </Box>
        )}

        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {/* Header */}
          <AppBar 
            position="sticky" 
            elevation={0} 
            sx={{ 
              bgcolor: 'transparent',
              backdropFilter: 'none',
              borderBottom: 'none',
              zIndex: (t) => t.zIndex.drawer + 1,
            }}
          >
            <Toolbar sx={{ justifyContent: 'space-between', minHeight: { xs: 64, md: 72 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {isMobile && (
                  <IconButton
                    color="inherit"
                    edge="start"
                    onClick={handleDrawerToggle}
                    sx={{ mr: 2 }}
                  >
                    <MenuIcon />
                  </IconButton>
                )}
              </Box>
              
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title="View on GitHub">
                  <IconButton 
                    size="small" 
                    sx={{ 
                      color: 'text.secondary',
                      '&:hover': { color: 'primary.main', bgcolor: 'rgba(0, 229, 255, 0.05)' }
                    }}
                  >
                    <Code fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Toolbar>
          </AppBar>

          {/* Main Content */}
          <Box
            component="main"
            sx={{
              flex: 1,
              p: { xs: 2, md: 4, lg: 6 },
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              background: 'radial-gradient(circle at 50% 0%, rgba(0, 229, 255, 0.08) 0%, rgba(0, 0, 0, 0) 60%)',
            }}
          >
            <Box sx={{ width: '100%', maxWidth: 1400 }}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: { xs: 3, md: 5 }, 
                  borderRadius: 6,
                  bgcolor: alpha(theme.palette.background.paper, 0.6),
                  backdropFilter: 'blur(24px)',
                  border: '1px solid',
                  borderColor: 'rgba(255, 255, 255, 0.08)',
                  boxShadow: '0 24px 48px rgba(0,0,0,0.5)',
                  minHeight: 'calc(100vh - 160px)',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  overflow: 'visible',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: -1,
                    borderRadius: 'inherit',
                    padding: '1px',
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.15), rgba(255,255,255,0.02) 40%, rgba(255,255,255,0.02) 60%, rgba(255,255,255,0.1))',
                    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                    WebkitMaskComposite: 'xor',
                    maskComposite: 'exclude',
                    pointerEvents: 'none',
                  }
                }}
              >
                {selectedUtility.component}
              </Paper>
            </Box>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default App;
